/**
 * オペレーターマスタ(仕様書 §4.1 / 2026-09-09 スタッフ項目表)。
 * 2026-09-18 UIデザインシステム(docs/design-system.md)の「一覧＋詳細型」に改修:
 *  - 一覧は横幅いっぱい(DataTable)。管理番号・氏名を左に固定、50件/100件のページ送り
 *  - 行を押すと右に詳細パネル(基本情報・BB ID・単価改定履歴)。選択中のスタッフは URL(?op=)に持ち、戻るで閉じられる
 *  - 編集は詳細パネルの「編集」から(行内編集はしない)。admin=編集可 / viewer=閲覧のみ
 *  - スマホ(≤900px)はカード一覧。詳細パネルは全画面(比較用に ?mobile=stack で既存の縦積み)
 *  - readOnly(デザイン比較の画面で使う。D-132): 新規登録・編集・仮登録を出さず、書き込みを一切しない(管理者でも閲覧だけ)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import MultiSelect, { matchesFilter } from '@core/components/MultiSelect';
import { SearchBox, matchKeyword } from '@core/components/tableTools';
import { useToast } from '@core/components/Toast';
import DataTable, { type DataTableColumn } from '@core/components/ui/DataTable';
import { SplitView } from '@core/components/ui/DetailPanel';
import FilterBar from '@core/components/ui/FilterBar';
import PageHeader from '@core/components/ui/PageHeader';
import AlertPanel, { type AlertItem } from '@core/components/ui/AlertPanel';
import StatusBadge from '@core/components/ui/StatusBadge';
import { fetchUnknownBbIds, guessFromBbId, ignoreUnknownBbId, removeUnknownBbId, type UnknownBbId } from './unknownBbRepo';
import { fmtTs } from '@core/lib/auditLog';
import {
  BUSINESS_CODES,
  BUSINESS_LABELS,
  OPERATOR_STATUSES,
  OPERATOR_STATUS_LABELS,
  PAY_TYPE_CODES,
  PAY_TYPE_LABELS,
  WORK_STYLE_CODES,
  WORK_STYLE_CODE_LABELS,
  WORK_STYLE_LABELS,
  type BusinessType,
  type Company,
  type Operator,
  type OperatorStatus,
  type PayType,
  type WorkStyle,
} from '@core/lib/types';
import { formatCount, formatCurrency, NO_DATA } from '@core/utils/formatters';
import { appendOldBbAccounts, createProvisionalOperator, fetchOperators } from './operatorRepo';
import UnknownBbBulkModal from './UnknownBbBulkModal';
import UnknownBbLinkModal, { type UnknownBbChoice } from './UnknownBbLinkModal';
import { bulkLinkRows, linkCandidates, oldAccountFor, planBulkRegister } from './unknownBbLink';
import { fetchCompanies } from './companyRepo';
import OperatorEditModal from './OperatorEditModal';
import BbReissueModal from './BbReissueModal';
import OperatorDetailPanel, { StatusDot } from './OperatorDetailPanel';

const STATUS_OPTIONS = OPERATOR_STATUSES.map((key) => ({ key, label: OPERATOR_STATUS_LABELS[key] }));
// 2026-09-15 MTG のコード(M/A/H/T・H/C・A/B)を名前の前に出す
const WORK_STYLE_OPTIONS = (Object.keys(WORK_STYLE_LABELS) as WorkStyle[]).map((key) => ({ key, label: `${WORK_STYLE_CODES[key]} ${WORK_STYLE_CODE_LABELS[key]}` }));
const PAY_TYPE_OPTIONS = (Object.keys(PAY_TYPE_LABELS) as PayType[]).map((key) => ({ key, label: `${PAY_TYPE_CODES[key]} ${PAY_TYPE_LABELS[key]}` }));
const BUSINESS_OPTIONS = (Object.keys(BUSINESS_LABELS) as BusinessType[]).map((key) => ({ key, label: `${BUSINESS_CODES[key]} ${BUSINESS_LABELS[key]}` }));

const tsMillis = (ts: unknown): number =>
  ts && typeof (ts as { toMillis?: () => number }).toMillis === 'function' ? (ts as { toMillis: () => number }).toMillis() : 0;

const uniq = (values: string[]) => Array.from(new Set(values));

// 一覧の表示文字列(並べ替えにも同じ文字列を使う)。架電会社は一覧に出さない(2026-09-24 発注者指示)
const businessText = (o: Operator) => uniq(o.bb_accounts.map((a) => `${BUSINESS_CODES[a.business]} ${BUSINESS_LABELS[a.business]}`)).join('／');
const workStyleText = (o: Operator) => uniq(o.bb_accounts.map((a) => `${WORK_STYLE_CODES[a.work_style ?? o.work_style]} ${WORK_STYLE_CODE_LABELS[a.work_style ?? o.work_style]}`)).join('／');
const payTypeText = (o: Operator) => uniq(o.bb_accounts.map((a) => `${PAY_TYPE_CODES[a.pay_type ?? o.pay_type]} ${PAY_TYPE_LABELS[a.pay_type ?? o.pay_type]}`)).join('／');
/** 交通費は出勤のBB IDがある人だけ(無い人は対象外 —) */
const hasShinagawa = (o: Operator) => o.bb_accounts.some((a) => (a.work_style ?? o.work_style) === 'shinagawa');

function BbIds({ o }: { o: Operator }) {
  const first = o.bb_accounts[0];
  if (!first) return <span className="hint">{NO_DATA}</span>;
  return (
    <span className="op-bb" title={o.bb_accounts.map((a) => `${a.bb_id}${a.ext ? `(${a.ext})` : ''}`).join(' / ')}>
      {first.bb_id}
      {first.ext ? `(${first.ext})` : ''}
      {o.bb_accounts.length > 1 && <span className="op-more">他{o.bb_accounts.length - 1}</span>}
    </span>
  );
}

/** 先頭のBB IDの時給／単価。単位(/h・/件)は幅をそろえた別要素にして、金額の桁を縦にそろえる */
function Wage({ o }: { o: Operator }) {
  const first = o.bb_accounts[0];
  if (!first) return <>{NO_DATA}</>;
  const others = o.bb_accounts.length - 1;
  const commission = (first.pay_type ?? o.pay_type) === 'commission';
  // 時給がまだ入っていないアカウント(仮登録)は金額を出さず「未設定」と出す(D-100)
  if (o.bb_accounts.some((a) => a.wage_pending)) {
    return (
      <>
        {others > 0 && <span className="dt-note">他{others}</span>}
        <StatusBadge kind="notEntered" label="時給未設定" />
      </>
    );
  }
  return (
    <>
      {others > 0 && <span className="dt-note">他{others}</span>}
      {formatCurrency(first.current_wage)}
      <span className="dt-unit">{commission ? '/件' : '/h'}</span>
    </>
  );
}

export default function OperatorsPage({ readOnly = false }: { readOnly?: boolean } = {}) {
  const { isAdmin, user } = useAuth();
  /** 登録・編集・仮登録ができるか。readOnly のときは管理者でも出さない(書き込みはすべてこの判定を通る) */
  const canEdit = isAdmin && !readOnly;
  const toast = useToast();
  /** 未登録のBB ID(CSV取込で見つかったもの)。ボタン1つで仮登録できる(D-100) */
  const [unknownBb, setUnknownBb] = useState<UnknownBbId[]>([]);
  const [addingBb, setAddingBb] = useState<string | null>(null);
  const [linking, setLinking] = useState<UnknownBbId | null>(null);
  const [bulkLinking, setBulkLinking] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  /** 未登録のBB IDで選んだもの(まとめて 登録する/登録しない。2026-10-17) */
  const [unknownSel, setUnknownSel] = useState<Set<string>>(() => new Set());
  const [params, setParams] = useSearchParams();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState<Set<OperatorStatus>>(() => new Set<OperatorStatus>(['active']));
  const [workStyles, setWorkStyles] = useState<Set<WorkStyle>>(() => new Set());
  const [payTypes, setPayTypes] = useState<Set<PayType>>(() => new Set());
  const [businesses, setBusinesses] = useState<Set<BusinessType>>(() => new Set());
  /** 架電会社は一覧・絞り込みに出さない(2026-09-24)。BB ID ごとの会社は編集画面で持つ */
  const [companyIds] = useState<Set<string>>(() => new Set());
  const [keyword, setKeyword] = useState('');
  /** 'new'=新規登録 / Operator=編集 */
  const [editing, setEditing] = useState<Operator | 'new' | null>(null);
  const [reissuing, setReissuing] = useState(false);

  const selectedId = params.get('op');
  // スマホはカード一覧(docs/design-system.md §6-2)。比較用に ?mobile=stack で既存の縦積みも出せる(発注者の確認後に外す)
  const mobileMode = params.get('mobile') === 'stack' ? 'stack' : 'cards';
  const select = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('op', id);
    else p.delete('op');
    setParams(p);
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [ops, cos] = await Promise.all([fetchOperators(), fetchCompanies()]);
      setOperators(ops);
      setCompanies(cos);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(
    () =>
      // BB ID 単位の条件(出勤形態/報酬区分/架電内容/架電会社)は、いずれかのBB IDが一致すれば表示する
      operators
        .filter((o) => matchesFilter(statuses, o.status))
        .filter((o) => workStyles.size === 0 || o.bb_accounts.some((a) => matchesFilter(workStyles, a.work_style ?? o.work_style)))
        .filter((o) => payTypes.size === 0 || o.bb_accounts.some((a) => matchesFilter(payTypes, a.pay_type ?? o.pay_type)))
        .filter((o) => businesses.size === 0 || o.bb_accounts.some((a) => matchesFilter(businesses, a.business)))
        .filter((o) => companyIds.size === 0 || o.bb_accounts.some((a) => matchesFilter(companyIds, a.company_id)))
        .filter((o) => matchKeyword(keyword, o.name, o.op_code, ...o.bb_accounts.flatMap((a) => [a.bb_id, a.ext]))),
    [operators, statuses, workStyles, payTypes, businesses, companyIds, keyword],
  );

  const selected = selectedId ? (operators.find((o) => o.id === selectedId) ?? null) : null;

  const columns: DataTableColumn<Operator>[] = [
    { key: 'op_code', header: '管理番号', width: 96, sticky: true, nowrap: true, sortValue: (o) => o.op_code, render: (o) => o.op_code },
    { key: 'name', header: '氏名', width: 150, sticky: true, nowrap: true, sortValue: (o) => o.name, render: (o) => <span className="op-name">{o.name}</span> },
    { key: 'bb_id', header: 'BB ID(内線)', width: 160, nowrap: true, sortValue: (o) => o.bb_accounts[0]?.bb_id, render: (o) => <BbIds o={o} /> },
    { key: 'business', header: '架電内容', width: 110, nowrap: true, sortValue: businessText, render: businessText },
    { key: 'work_style', header: '出勤形態', width: 120, nowrap: true, sortValue: workStyleText, render: workStyleText },
    { key: 'pay_type', header: '報酬区分', width: 100, nowrap: true, sortValue: payTypeText, render: payTypeText },
    { key: 'wage', header: '時給／単価', width: 140, numeric: true, sortValue: (o) => o.bb_accounts[0]?.current_wage, render: (o) => <Wage o={o} /> },
    { key: 'commute', header: '交通費(往復)', width: 120, numeric: true, sortValue: (o) => (hasShinagawa(o) ? o.commute_cost : null), render: (o) => (hasShinagawa(o) ? formatCurrency(o.commute_cost) : NO_DATA) },
    { key: 'status', header: '在籍', width: 90, nowrap: true, sortValue: (o) => OPERATOR_STATUSES.indexOf(o.status), render: (o) => <StatusDot status={o.status} /> },
    { key: 'created_at', header: '登録日', width: 110, align: 'center', sortValue: (o) => tsMillis(o.created_at) || null, render: (o) => fmtTs(o.created_at, false) },
  ];
  // 一覧の一番右に「編集」(2026-09-24 発注者指示)。行を選ぶ(詳細を開く)のとは別に、その場で編集を開ける
  if (canEdit) {
    columns.push({
      key: 'edit',
      header: '編集',
      width: 76,
      align: 'center',
      render: (o) => (
        <button
          type="button"
          className="ui-btn ui-btn-secondary ui-btn-sm"
          aria-label={`${o.name} を編集`}
          onClick={(e) => {
            e.stopPropagation();
            setEditing(o);
          }}
        >
          編集
        </button>
      ),
    });
  }

  const mobileCard = (o: Operator) => (
    <>
      <div className="op-card-head">
        <span className="op-name">{o.name}</span>
        <StatusDot status={o.status} />
      </div>
      <div className="op-card-meta">
        {o.op_code} ・ <BbIds o={o} />
      </div>
      <div className="op-card-row">
        <span>出勤形態／報酬区分</span>
        <span>
          {workStyleText(o)}／{payTypeText(o)}
        </span>
      </div>
      <div className="op-card-row">
        <span>時給／単価</span>
        <span className="is-numeric">
          <Wage o={o} />
        </span>
      </div>
    </>
  );

  const moreActiveCount = [workStyles, payTypes, businesses, companyIds].filter((s) => s.size > 0).length;

  const reloadUnknown = useCallback(() => {
    if (!canEdit) return;
    void fetchUnknownBbIds()
      .then(setUnknownBb)
      .catch(() => setUnknownBb([]));
  }, [canEdit]);
  useEffect(() => reloadUnknown(), [reloadUnknown]);

  /**
   * 未登録のBB ID の登録(D-206)。名前が同じスタッフがいれば旧IDとして結び付け、いなければ新しい人として仮登録する。
   * 昔のIDを別の人として仮登録して同じ人が2人にならないように、画面で選んでから登録する
   */
  async function registerUnknown(u: UnknownBbId, choice: UnknownBbChoice) {
    if (!user || !canEdit) return;
    setAddingBb(u.bb_id);
    try {
      if (choice.kind === 'link') {
        await appendOldBbAccounts(choice.operator, [oldAccountFor(u, choice.operator)], operators, user.id);
        toast(`${u.bb_id} を ${choice.operator.name} の旧IDとして結び付けました。${u.first_date}〜${u.last_date} を取り込み直すと過去の数字に入ります`);
      } else if (choice.kind === 'ignore') {
        await ignoreUnknownBbId(u.bb_id);
        toast(`${u.bb_id} を登録しないにしました(一覧から外しました)`);
        setLinking(null);
        reloadUnknown();
        return;
      } else {
        await createProvisionalOperator({ bb_id: u.bb_id, name: u.name, ...guessFromBbId(u.bb_id, u.name) }, user.id);
        toast(`${u.bb_id} を仮登録しました。時給を入れてください`);
      }
      await removeUnknownBbId(u.bb_id);
      setLinking(null);
      await reload();
      reloadUnknown();
    } catch (e) {
      toast(e instanceof Error ? e.message : '登録に失敗しました');
    } finally {
      setAddingBb(null);
    }
  }

  async function ignoreUnknown(u: UnknownBbId) {
    if (!canEdit) return;
    if (!confirm(`${u.bb_id}${u.name ? `(${u.name})` : ''} を登録しないにします。一覧から外れ、このIDの稼働・取得は集計に入りません。よろしいですか?`)) return;
    setAddingBb(u.bb_id);
    try {
      await ignoreUnknownBbId(u.bb_id);
      toast(`${u.bb_id} を登録しないにしました(一覧から外しました)`);
      reloadUnknown();
    } catch (e) {
      toast(e instanceof Error ? e.message : '登録しないにできませんでした');
    } finally {
      setAddingBb(null);
    }
  }

  /** まとめて結び付ける(D-207)。同じ人の分は1回で書く */
  async function bulkLink(pairs: { unknown: UnknownBbId; operator: Operator }[]) {
    if (!user || !canEdit) return;
    setBulkBusy(true);
    let done = 0;
    try {
      const byOp = new Map<string, { operator: Operator; list: UnknownBbId[] }>();
      for (const p of pairs) {
        const g = byOp.get(p.operator.id) ?? { operator: p.operator, list: [] };
        g.list.push(p.unknown);
        byOp.set(p.operator.id, g);
      }
      for (const { operator, list } of byOp.values()) {
        await appendOldBbAccounts(operator, list.map((u) => oldAccountFor(u, operator)), operators, user.id);
        for (const u of list) await removeUnknownBbId(u.bb_id);
        done += list.length;
      }
      toast(`${done}件を結び付けました。表示された期間を取り込み直すと過去の数字に入ります`);
      setBulkLinking(false);
    } catch (e) {
      toast(e instanceof Error ? `${done}件まで結び付けました。止まった理由: ${e.message}` : '結び付けに失敗しました');
    } finally {
      setBulkBusy(false);
      await reload();
      reloadUnknown();
    }
  }

  /** 選んだ未登録のBB IDを まとめて登録しない(2026-10-17 発注者指示) */
  async function bulkIgnore(list: UnknownBbId[]) {
    if (!canEdit || list.length === 0) return;
    if (!confirm(`選んだ${list.length}件を登録しないにします。一覧から外れ、これらのIDの稼働・取得は集計に入りません。よろしいですか?`)) return;
    setBulkBusy(true);
    let done = 0;
    try {
      for (const u of list) {
        await ignoreUnknownBbId(u.bb_id);
        done++;
      }
      toast(`${done}件を登録しないにしました(一覧から外しました)`);
      setUnknownSel(new Set());
    } catch (e) {
      toast(e instanceof Error ? `${done}件まで登録しないにしました。止まった理由: ${e.message}` : '登録しないにできませんでした');
    } finally {
      setBulkBusy(false);
      reloadUnknown();
    }
  }

  /**
   * 選んだ未登録のBB IDを まとめて登録する(2026-10-17 発注者指示)。1件ずつの「登録する」と同じ振り分け:
   * 名前が同じスタッフが1人に決まれば その人の旧IDとして結び付け、いなければ仮登録。決められないものは飛ばす
   */
  async function bulkRegister(list: UnknownBbId[]) {
    if (!user || !canEdit || list.length === 0) return;
    const plan = planBulkRegister(list, operators);
    const lines = [
      `選んだ${list.length}件を登録します。`,
      plan.link.length > 0 && `・名前が同じスタッフに結び付ける: ${plan.link.length}件(${plan.link.map((p) => `${p.unknown.bb_id}→${p.operator.name}`).join('、')})`,
      plan.provisional.length > 0 && `・新しい人として仮登録する: ${plan.provisional.length}件(${plan.provisional.map((u) => u.bb_id).join('、')})`,
      plan.skipped.length > 0 && `・自動で決められないので登録しない(1件ずつ選んでください): ${plan.skipped.length}件(${plan.skipped.map((u) => u.bb_id).join('、')})`,
      'よろしいですか?',
    ].filter(Boolean);
    if (plan.link.length + plan.provisional.length === 0) {
      toast('まとめて登録できるものがありません。1件ずつ「結び付ける」から選んでください');
      return;
    }
    if (!confirm(lines.join('\n'))) return;
    setBulkBusy(true);
    let done = 0;
    try {
      const byOp = new Map<string, { operator: Operator; list: UnknownBbId[] }>();
      for (const p of plan.link) {
        const g = byOp.get(p.operator.id) ?? { operator: p.operator, list: [] };
        g.list.push(p.unknown);
        byOp.set(p.operator.id, g);
      }
      for (const { operator, list: us } of byOp.values()) {
        await appendOldBbAccounts(operator, us.map((u) => oldAccountFor(u, operator)), operators, user.id);
        for (const u of us) await removeUnknownBbId(u.bb_id);
        done += us.length;
      }
      for (const u of plan.provisional) {
        await createProvisionalOperator({ bb_id: u.bb_id, name: u.name, ...guessFromBbId(u.bb_id, u.name) }, user.id);
        await removeUnknownBbId(u.bb_id);
        done++;
      }
      toast(
        `${done}件を登録しました${plan.provisional.length > 0 ? '。仮登録した人は時給を入れてください' : ''}${plan.link.length > 0 ? '。結び付けた分は、その期間を取り込み直すと過去の数字に入ります' : ''}${plan.skipped.length > 0 ? `。${plan.skipped.length}件は1件ずつ選んでください` : ''}`,
      );
      setUnknownSel(new Set(plan.skipped.map((u) => u.bb_id)));
    } catch (e) {
      toast(e instanceof Error ? `${done}件まで登録しました。止まった理由: ${e.message}` : '登録に失敗しました');
    } finally {
      setBulkBusy(false);
      await reload();
      reloadUnknown();
    }
  }

  // マスタに登録済みになった ID(付け直し(CSV)などで)は出さない
  const registeredBb = new Set(operators.flatMap((o) => o.bb_accounts.map((a) => a.bb_id.toUpperCase())));
  const unknownOpen = unknownBb.filter((u) => !u.ignored && !registeredBb.has(u.bb_id.toUpperCase()));
  const bulkCount = bulkLinkRows(unknownOpen, operators).length;
  const selectedUnknown = unknownOpen.filter((u) => unknownSel.has(u.bb_id));
  const unknownItems: AlertItem[] = unknownOpen.map((u) => {
    const g = guessFromBbId(u.bb_id, u.name);
    return {
      id: u.bb_id,
      severity: 'warning',
      target: `${u.bb_id}${u.name ? ` ${u.name}` : ''}`,
      message: 'オペレーターマスタに無いため、この人の稼働・取得が集計に入っていません',
      diff: `${WORK_STYLE_LABELS[g.work_style]}／${PAY_TYPE_LABELS[g.pay_type]}／${BUSINESS_LABELS[g.business]}(BB IDからの推測) ・ ${u.first_date} 〜 ${u.last_date}`,
      // 名前が同じスタッフがいれば「結び付ける」(選ぶ画面を開く)。いなければ「登録する」(仮登録も選べる)
      action: { label: linkCandidates(u, operators).length > 0 ? '結び付ける' : '登録する', onClick: () => setLinking(u) },
      // 「登録しない」は一覧から直接押せるようにする(2026-10-12 発注者指摘「登録しないが表示されていません」)
      subActions: [{ label: '登録しない', onClick: () => void ignoreUnknown(u), disabled: addingBb === u.bb_id }],
    };
  });

  return (
    <div className="screen full is-fill">
      <SplitView
        panel={
          selected && (
            <OperatorDetailPanel operator={selected} companies={companies} canEdit={canEdit} onEdit={() => setEditing(selected)} onClose={() => select(null)} />
          )
        }
      >
        <PageHeader
          title="スタッフ一覧"
          meta={loading ? '読み込み中' : `${formatCount(filtered.length)}名(全${formatCount(operators.length)}名)`}
          actions={
            canEdit && (
              <>
                <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setReissuing(true)} disabled={loading}>
                  BB IDの付け直し(CSV)
                </button>
                <button type="button" className="ui-btn ui-btn-primary" onClick={() => setEditing('new')}>
                  ＋ 新規登録
                </button>
              </>
            )
          }
        />
        {canEdit && unknownItems.length > 0 && (
          <AlertPanel title="未登録のBB ID" meta="CSV取込で見つかった、マスタに無いBB IDです。いるスタッフの昔のIDは「結び付ける」、いない人は「登録する」で仮登録か登録しない。過去の日の数字は、その期間を取り込み直すと入ります" items={unknownItems}
            selection={{ selected: unknownSel, onChange: setUnknownSel }}
            actions={
              selectedUnknown.length > 0 ? (
                <>
                  <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => void bulkIgnore(selectedUnknown)} disabled={bulkBusy}>
                    選んだ{selectedUnknown.length}件を登録しない
                  </button>
                  <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" onClick={() => void bulkRegister(selectedUnknown)} disabled={bulkBusy}>
                    選んだ{selectedUnknown.length}件を登録する
                  </button>
                </>
              ) : (
                bulkCount > 0 && (
                  <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setBulkLinking(true)}>
                    名前が同じ人にまとめて結び付ける({bulkCount}件)
                  </button>
                )
              )
            }
          />
        )}
        <FilterBar
          moreActiveCount={moreActiveCount}
          more={
            <>
              <MultiSelect label="出勤形態" options={WORK_STYLE_OPTIONS} value={workStyles} onChange={setWorkStyles} />
              <MultiSelect label="報酬区分" options={PAY_TYPE_OPTIONS} value={payTypes} onChange={setPayTypes} />
              <MultiSelect label="架電内容" options={BUSINESS_OPTIONS} value={businesses} onChange={setBusinesses} />
            </>
          }
        >
          <SearchBox className="ui-search" value={keyword} onChange={setKeyword} placeholder="名前・BB ID・内線で検索" />
          <MultiSelect label="在籍" options={STATUS_OPTIONS} value={statuses} onChange={setStatuses} />
        </FilterBar>
        <DataTable
          caption="スタッフ一覧"
          columns={columns}
          rows={filtered}
          rowKey={(o) => o.id}
          loading={loading}
          emptyMessage={operators.length === 0 ? '登録されたスタッフがいません(CSVインポートまたは新規登録で登録してください)' : '条件に当てはまるスタッフがいません'}
          onRowClick={(o) => select(o.id)}
          selectedKey={selected?.id ?? null}
          mobile={mobileMode}
          renderMobileCard={mobileCard}
        />
      </SplitView>

      {bulkLinking && canEdit && (
        <UnknownBbBulkModal unknowns={unknownOpen} operators={operators} busy={bulkBusy} onClose={() => setBulkLinking(false)} onSubmit={(p) => void bulkLink(p)} />
      )}
      {linking && canEdit && (
        <UnknownBbLinkModal unknown={linking} operators={operators} busy={addingBb === linking.bb_id} onClose={() => setLinking(null)} onSubmit={(c) => void registerUnknown(linking, c)} />
      )}
      {reissuing && canEdit && user && (
        <BbReissueModal
          operators={operators}
          userId={user.id}
          onClose={() => setReissuing(false)}
          onDone={() => {
            setReissuing(false);
            void reload();
          }}
        />
      )}
      {editing && canEdit && (
        <OperatorEditModal
          operator={editing === 'new' ? null : editing}
          operators={operators}
          companies={companies}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
    </div>
  );
}
